import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { AppConstants } from '../constants/app-constants';
import {
  buildNavGroupsForPortal,
  resolveSidebarPortal,
  type SidebarNavGroup,
} from '../constants/sidebar-access';
import { AuthService } from '../services/auth.service';
import { Announcement, AnnouncementsService } from '../services/announcements.service';
import { LanguageService } from '../services/language.service';

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.css']
})
export class MainComponent implements OnInit, OnDestroy {
  collapsed = false;
  hideChrome = false;
  isTeacherPortal = false;
  isSupervisorPortal = false;
  userName = 'User';
  userRole = '';
  userInitials = 'U';
  navGroups: SidebarNavGroup[] = [];
  activeAnnouncements: Announcement[] = [];
  currentAnnouncementIndex = 0;
  showLoginCarousel = false;

  private readonly carouselSeenKey = 'weekly-plan-carousel-seen';
  private routerSub?: Subscription;
  private userSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private announcementsService: AnnouncementsService,
    private languageService: LanguageService,
  ) { }

  get userRoleLabel(): string {
    return this.languageService.roleLabel(this.userRole);
  }

  get currentAnnouncement(): Announcement | null {
    return this.activeAnnouncements[this.currentAnnouncementIndex] ?? null;
  }

  get hasMultipleAnnouncements(): boolean {
    return this.activeAnnouncements.length > 1;
  }

  ngOnInit(): void {
    this.collapsed = localStorage.getItem(AppConstants.SIDEBAR_COLLAPSED) === 'true';
    this.isTeacherPortal = this.route.snapshot.data['teacherPortal'] === true
      || this.authService.isTeacher();
    this.isSupervisorPortal = this.route.snapshot.data['supervisorPortal'] === true
      || this.authService.isSupervisor();
    this.refreshNav();
    this.loadUserProfile();
    this.syncChrome();
    this.routerSub = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.syncChrome());
    this.userSub = this.authService.userChanges$.subscribe(() => this.refreshNav());
    void this.authService.getProfile().catch(() => undefined);
    if (this.isTeacherPortal) {
      void this.loadActiveAnnouncements();
    }
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    this.userSub?.unsubscribe();
  }

  closeLoginCarousel(): void {
    sessionStorage.setItem(this.carouselSeenKey, 'true');
    this.showLoginCarousel = false;
  }

  previousAnnouncement(): void {
    if (!this.activeAnnouncements.length) {
      return;
    }
    this.currentAnnouncementIndex = this.currentAnnouncementIndex === 0
      ? this.activeAnnouncements.length - 1
      : this.currentAnnouncementIndex - 1;
  }

  nextAnnouncement(): void {
    if (!this.activeAnnouncements.length) {
      return;
    }
    this.currentAnnouncementIndex = (this.currentAnnouncementIndex + 1) % this.activeAnnouncements.length;
  }

  goToAnnouncement(index: number): void {
    if (index >= 0 && index < this.activeAnnouncements.length) {
      this.currentAnnouncementIndex = index;
    }
  }

  toggleSidebar(): void {
    this.collapsed = !this.collapsed;
    localStorage.setItem(AppConstants.SIDEBAR_COLLAPSED, String(this.collapsed));
  }

  private refreshNav(): void {
    const user = this.authService.getUser();
    const portal = this.isSupervisorPortal
      ? 'supervisor'
      : this.isTeacherPortal
        ? 'teacher'
        : resolveSidebarPortal(user?.role?.name || user?.role, {
          isAdmin: user?.isAdmin,
          isTeacher: user?.isTeacher,
          isSupervisor: user?.isSupervisor,
        });
    this.navGroups = buildNavGroupsForPortal(portal, user?.sidebarAccess);
  }

  private syncChrome(): void {
    let child = this.route.firstChild;
    while (child?.firstChild) {
      child = child.firstChild;
    }
    this.hideChrome = child?.snapshot.data?.['hideChrome'] === true;
  }

  private loadUserProfile(): void {
    const user = this.authService.getUser();
    const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
    this.userName = fullName || user?.name || user?.username || 'User';

    const role = user?.role?.name || user?.role;
    this.userRole = role ? String(role) : '';

    this.userInitials = this.userName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part: string) => part[0].toUpperCase())
      .join('') || 'U';
  }

  private async loadActiveAnnouncements(): Promise<void> {
    try {
      this.activeAnnouncements = await this.announcementsService.listActive();
      this.currentAnnouncementIndex = 0;
      this.showLoginCarousel = this.activeAnnouncements.length > 0
        && sessionStorage.getItem(this.carouselSeenKey) !== 'true';
    } catch {
      this.activeAnnouncements = [];
      this.showLoginCarousel = false;
    }
  }
}
